import { Building2, MapPin, Pencil, Truck } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { LineOfBusiness, type OrgBusiness, TRADE_LABELS } from '@/models/business'
import { EditBusinessAddressDialog } from './EditBusinessAddressDialog'
import { EditBusinessDialog } from './EditBusinessDialog'

interface BusinessCardsProps {
  business: OrgBusiness | null
  orgId: string
  onUpdated: () => void
}

export function BusinessCards({ business, orgId, onUpdated }: BusinessCardsProps) {
  const [editCarrierOpen, setEditCarrierOpen] = useState(false)
  const [editAddressOpen, setEditAddressOpen] = useState(false)

  if (!business) {
    return (
      <Card className="gap-3">
        <CardContent>
          <p className="text-muted-foreground text-sm">No motor carrier associated with this organization.</p>
        </CardContent>
      </Card>
    )
  }

  const contractor = !!business.lineOfBusiness && business.lineOfBusiness !== LineOfBusiness.TRUCKING

  const address = business.address
  return (
    <>
      <EditBusinessDialog
        open={editCarrierOpen}
        onOpenChange={setEditCarrierOpen}
        onSuccess={onUpdated}
        business={business}
        orgId={orgId}
      />
      <EditBusinessAddressDialog
        open={editAddressOpen}
        onOpenChange={setEditAddressOpen}
        onSuccess={onUpdated}
        business={business}
        orgId={orgId}
      />

      <Card className="gap-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {contractor ? <Building2 className="size-4" /> : <Truck className="size-4" />}
            {contractor ? 'Business' : 'Motor Carrier'}
          </CardTitle>
          <CardAction>
            <Button size="icon-sm" variant="outline" onClick={() => setEditCarrierOpen(true)}>
              <Pencil className="size-3.5" />
              <span className="sr-only">Edit motor carrier</span>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div>
            <span className="text-muted-foreground">Name:</span> {business.name || '—'}
          </div>
          {contractor ? (
            <div>
              <span className="text-muted-foreground">Work:</span>{' '}
              {business.businessDescription || (business.trade && TRADE_LABELS[business.trade]) || '—'}
            </div>
          ) : (
            <>
              <div>
                <span className="text-muted-foreground">DOT Number:</span> {business.dotNumber || '—'}
              </div>
              <div>
                <span className="text-muted-foreground">MC Number:</span> {business.mcNumber || '—'}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card className="gap-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="size-4" />
            Address
          </CardTitle>
          <CardAction>
            <Button size="icon-sm" variant="outline" onClick={() => setEditAddressOpen(true)}>
              <Pencil className="size-3.5" />
              <span className="sr-only">Edit address</span>
            </Button>
          </CardAction>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {address ? (
            <div className="space-y-1">
              <div>{address.line1 || '—'}</div>
              {address.line2 && <div>{address.line2}</div>}
              <div>
                {[address.city, address.state].filter(Boolean).join(', ')} {address.zip}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">No address on file.</p>
          )}
        </CardContent>
      </Card>
    </>
  )
}
